import { getAuth } from 'firebase/auth'
import { saveAs } from 'file-saver'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { z } from 'zod'
import { kUserFilesApiBasePath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { formatUserFilesErrorDetail } from '@/user_files/utils/api-error'

const SignedDownloadUrlResponseSchema = z.object({
  file_id: z.string(),
  download_url: z.string(),
  expires_at: z.string(),
})

type DownloadFileArgs = {
  /** The user_file id to sign for download. */
  fileId: string
  /** The path to the file in the user-files pipeline bucket. */
  storagePath: string
  /** The name of the file to save as. */
  fileName?: string
}

function resolveFileName(storagePath: string, fileName?: string) {
  const pathTail = storagePath.split('/').pop()?.trim()
  return fileName ?? decodeURIComponent(pathTail && pathTail.length > 0 ? pathTail : 'download')
}

async function getSignedDownloadUrl(fileId: string, signal?: AbortSignal) {
  const sentryTransactionId = nanoid()
  const auth = getAuth()
  const token = await auth.currentUser?.getIdToken()

  if (!token) {
    throw new Error('User not authenticated')
  }

  const url = `${kPaxtonAppApiRESTBaseUrl()}${kUserFilesApiBasePath}/${encodeURIComponent(fileId)}/download-url`
  const response = await fetch(url, {
    method: 'GET',
    ...(signal ? { signal } : {}),
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Transaction-Id': sentryTransactionId,
    },
  })

  const { data, parseError } = await readResponseJson(response)
  if (!response.ok) {
    const errorData = parseError ? {} : data
    const detail = formatUserFilesErrorDetail(errorData)
    throw new ApiError(response.status, detail || `Failed to create download URL: ${response.status}`)
  }

  if (parseError) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: sentryTransactionId })
      Sentry.captureException(new Error('User file download URL response JSON parse failed'), {
        extra: {
          ...getResponseDebugContext(response, url),
          fileId,
          parseError: formatUnknownError(parseError),
        },
      })
    })

    throw new ApiResponsePayloadError(response.status, 'Invalid user file download URL response JSON')
  }

  const parsed = SignedDownloadUrlResponseSchema.safeParse(data)
  if (!parsed.success) {
    Sentry.withScope((scope) => {
      scope.setTags({ transaction_id: sentryTransactionId })
      Sentry.captureException(new Error('User file download URL response schema validation failed'), {
        extra: {
          ...getResponseDebugContext(response, url),
          fileId,
          responseKeys: getTopLevelKeys(data),
          issues: parsed.error.issues,
        },
      })
    })

    throw new ApiResponsePayloadError(response.status, 'Invalid user file download URL response shape', parsed.error.issues)
  }

  return parsed.data.download_url
}

async function fetchBlobFromUrl(downloadUrl: string, context: { fileId?: string; resolvedFileName: string }, signal?: AbortSignal): Promise<Blob> {
  const response = signal ? await fetch(downloadUrl, { signal }) : await fetch(downloadUrl)

  if (!response.ok) {
    console.error(`Download failed with status ${response.status}`, context)
    throw new Error(`Download failed with status ${response.status}`)
  }

  return response.blob()
}

/** Read a matter file's original bytes, for viewers that render the file itself. Nothing is saved. */
export async function fetchOriginalUserFile(fileId: string, fileName: string, signal: AbortSignal): Promise<File> {
  const downloadUrl = await getSignedDownloadUrl(fileId, signal)
  const blob = await fetchBlobFromUrl(downloadUrl, { fileId, resolvedFileName: fileName }, signal)
  return new File([blob], fileName, { type: blob.type })
}

async function downloadViaSignedUrl(fileId: string, resolvedFileName: string): Promise<void> {
  const downloadUrl = await getSignedDownloadUrl(fileId)
  const blob = await fetchBlobFromUrl(downloadUrl, { fileId, resolvedFileName })
  saveAs(blob, resolvedFileName)
}

/** Download a user file through a backend-authorized signed URL. */
export async function downloadFile({ fileId, storagePath, fileName }: DownloadFileArgs): Promise<void> {
  const resolvedFileName = resolveFileName(storagePath, fileName)
  await downloadViaSignedUrl(fileId, resolvedFileName)
}
